<template>
    <div id="ChatbotSandbox">
        <div class="header">
            <div class="header-icon">
                <i class="material-icons">delete_outline</i>
            </div>
            <div class="header-text">
                <h1>{{ $t("chatbotv2.sandboxPage.title") }}</h1>
                <p>{{ $t("chatbotv2.sandboxPage.description") }}</p>
            </div>
        </div>

        <div v-if="loading" class="stories-loading">
            <div v-for="skltn in 3" class="story story-skeleton" :key="skltn">
                <a-skeleton :active="true" :paragraph="{ rows: 3 }" />
            </div>
        </div>

        <div v-else-if="deletedStories.length === 0" class="empty-state">
            <i class="material-icons">delete_outline</i>
            <p>{{ $t("chatbotv2.sandboxPage.emptyState") }}</p>
        </div>

        <div v-else class="stories">
            <div
                class="story story-deleted"
                v-for="story in deletedStories"
                :key="story.id"
            >
                <div class="story-header">
                    <div class="story-title">{{ story.title || $t("chatbotv2.sandboxPage.unnamedScenario") }}</div>
                    <a-popover trigger="click" class="story-menu-content">
                        <div slot="content">
                            <div
                                class="story-menu-popover-items"
                                @click="restoreStory(story)"
                            >
                                <span><a-icon type="undo" class="px-1" /></span>
                                <span>{{ $t("chatbotv2.sandboxPage.restore") }}</span>
                            </div>
                            <hr style="margin-bottom: 0.2rem; margin-top: 0.2rem" />
                            <div
                                class="story-menu-popover-items"
                                @click="permanentlyDeleteStory(story)"
                            >
                                <span><a-icon type="delete" class="px-1" /></span>
                                <span>{{ $t("chatbotv2.sandboxPage.permanentlyDelete") }}</span>
                            </div>
                        </div>
                        <a-button
                            class="story-menu-btn"
                            @click="(e) => e.stopPropagation()"
                        >
                            <span class="material-icons">more_vert</span>
                        </a-button>
                    </a-popover>
                </div>
                <div class="story-content">
                    <div class="story-description" v-if="story.description">
                        {{ story.description }}
                    </div>
                    <div class="story-meta">
                        <div class="meta-item">
                            <i class="material-icons">schedule</i>
                            <span>{{ formatDeletedDate(story.deletedAt) }}</span>
                        </div>
                        <div class="meta-item" v-if="story.deletedBy">
                            <i class="material-icons">person</i>
                            <span>{{ getDeletedByText(story.deletedBy) }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import { CHATBOT_API } from "../../../../../api/chatbotv2";
import moment from "moment";

export default {
    name: "ChatbotSandbox",
    data() {
        return {
            loading: true,
        };
    },
    computed: {
        ...mapState({
            deletedStories: (state) => state.CB.deletedStories,
        }),
    },
    methods: {
        ...mapActions({
            listDeletedStories: "CB/listDeletedStories",
            deleteStory: "CB/deleteStory",
        }),
        formatDeletedDate(date) {
            if (!date) return this.$t("chatbotv2.sandboxPage.unknown");
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        getDeletedByText(deletedBy) {
            if (!deletedBy) return this.$t("chatbotv2.sandboxPage.unknown");
            // deletedBy populate edilmişse name veya email göster
            if (typeof deletedBy === "object" && deletedBy.name) {
                return deletedBy.name;
            }
            if (typeof deletedBy === "object" && deletedBy.email) {
                return deletedBy.email;
            }
            // Eğer sadece ID ise
            return this.$t("chatbotv2.sandboxPage.user");
        },
        async restoreStory(story) {
            const storyTitle = story.title || this.$t("chatbotv2.sandboxPage.unnamedScenario");
            this.$confirm({
                title: this.$t("chatbotv2.sandboxPage.restoreConfirmTitle"),
                content: this.$t("chatbotv2.sandboxPage.restoreConfirmContent", { story: storyTitle }),
                onOk: async () => {
                    try {
                        await CHATBOT_API.restoreStory(story.id);
                        this.$message.success(this.$t("chatbotv2.sandboxPage.restoreSuccess"));
                        await this.listDeletedStories();
                    } catch (error) {
                        console.error("Restore error:", error);
                        this.$message.error(error.response?.data?.error || this.$t("chatbotv2.sandboxPage.restoreError"));
                    }
                },
            });
        },
        async permanentlyDeleteStory(story) {
            this.$confirm({
                title: this.$t("chatbotv2.sandboxPage.permanentDeleteConfirmTitle"),
                content: this.$t("chatbotv2.sandboxPage.permanentDeleteConfirmContent"),
                okText: this.$t("chatbotv2.sandboxPage.permanentlyDelete"),
                okType: "danger",
                onOk: async () => {
                    try {
                        await CHATBOT_API.permanentlyDeleteStory(story.id);
                        this.$message.success(this.$t("chatbotv2.sandboxPage.permanentDeleteSuccess"));
                        await this.listDeletedStories();
                    } catch (error) {
                        console.error("Permanent delete error:", error);
                        this.$message.error(error.response?.data?.error || this.$t("chatbotv2.sandboxPage.permanentDeleteError"));
                    }
                },
            });
        },
    },
    async mounted() {
        try {
            await this.listDeletedStories();
        } catch (error) {
            this.$message.error(this.$t("chatbotv2.sandboxPage.loadError"));
        } finally {
            this.loading = false;
        }
    },
};
</script>
<style lang="scss" scoped>
#ChatbotSandbox {
    padding: 1.5rem;

    .header {
        display: flex;
        align-items: flex-start;
        padding: 1.5rem;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        margin-bottom: 1.5rem;

        .header-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: #f3f4f6;
            border-radius: 6px;
            margin-right: 1rem;
            flex-shrink: 0;

            .material-icons {
                font-size: 20px;
                color: #6b7280;
            }
        }

        .header-text {
            flex: 1;
            min-width: 0;

            h1 {
                font-size: 1.25rem;
                font-weight: 600;
                color: #111827;
                margin: 0 0 0.5rem 0;
            }

            p {
                font-size: 0.875rem;
                color: #6b7280;
                margin: 0;
                line-height: 1.5;
            }
        }
    }

    .stories-loading {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;

        .story-skeleton {
            width: 280px;
            height: 200px;
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            padding: 1rem;
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 4rem 2rem;
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        text-align: center;

        .material-icons {
            font-size: 64px;
            color: #d1d5db;
            margin-bottom: 1rem;
        }

        p {
            font-size: 1rem;
            color: #6b7280;
            margin: 0;
        }
    }

    .stories {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;

        .story {
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            width: 280px;
            min-width: 250px;
            max-width: 280px;
            display: flex;
            flex-direction: column;
            transition: all 0.2s;
            position: relative;

            &:hover {
                box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                border-color: #d1d5db;
            }

            &.story-deleted {
                opacity: 0.85;
                border-left: 3px solid #ef4444;
            }

            .story-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                padding: 1rem 1rem 0.5rem 1rem;
                border-bottom: 1px solid #f3f4f6;

                .story-title {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #111827;
                    flex: 1;
                    margin-right: 0.5rem;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .story-menu-btn {
                    border: none;
                    background: transparent;
                    padding: 0.25rem;
                    min-width: auto;
                    height: auto;
                    box-shadow: none;

                    .material-icons {
                        font-size: 20px;
                        color: #6b7280;
                    }

                    &:hover {
                        background: #f3f4f6;
                    }
                }
            }

            .story-content {
                padding: 1rem;
                flex: 1;
                display: flex;
                flex-direction: column;

                .story-description {
                    font-size: 0.875rem;
                    color: #6b7280;
                    margin-bottom: 1rem;
                    line-height: 1.5;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .story-meta {
                    margin-top: auto;
                    padding-top: 0.75rem;
                    border-top: 1px solid #f3f4f6;
                    display: flex;
                    flex-direction: column;
                    gap: 0.5rem;

                    .meta-item {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                        font-size: 0.75rem;
                        color: #9ca3af;

                        .material-icons {
                            font-size: 16px;
                        }
                    }
                }
            }
        }
    }
}

.story-menu-popover-items {
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        color: #1890ff;
        background: #f0f7ff;
    }
}
</style>
